import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchFile } from '@ffmpeg/util';
import { useQueryClient } from '@tanstack/react-query';

import { Modal } from '@/common';
import { Input } from '@/common/Input';
import { Button, Label, LABEL_TYPE, Spinner } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { ToggleSwitch } from '@/components/generic/ToggleSwitch/ToggleSwitch';
import { TOGGLE_THEMES } from '@/components/generic/ToggleSwitch/toggle-switch-config';
import { AssetScope, AssetType } from '@/enums/AssetEnums';
import { BlobLocation } from '@/enums/BlobLocationEnum';
import { getAssetBlobClient } from '@/helpers';
import { drawWaveformOnCanvas, generateWaveformData } from '@/helpers/WaveFormHelper';
import { getFFmpeg } from '@/helpers/ffmpeg';
import { showToast, ToastType } from '@/helpers/toast';
import { DraggableElement } from '@/pages/Event/components/DraggableElement';
import { PauseIcon, PlayIcon } from '@/resources';
import { useCreateAssetMutation, useCreateSasTokenMutation } from '@/services';
import { TestIds } from '@/shared/test-ids';
import { Asset } from '@/types';

const MIN_DURATION = 10;

const parseTimeToSeconds = (timeString: string): number => {
  if (!timeString || timeString.trim() === '') {
    return 0;
  }

  const parts = timeString.split(':');
  if (parts.length !== 2) {
    return 0;
  }

  const minutes = parseInt(parts[0]) || 0;
  const seconds = parseInt(parts[1]) || 0;
  return minutes * 60 + seconds;
};

const validateTimeInput = (value: string, maxSeconds?: number): string => {
  const cleaned = value.replace(/[^\d:]/g, '');

  if (cleaned.length <= 2) {
    return cleaned;
  } else if (cleaned.length === 3 && !cleaned.includes(':')) {
    return cleaned.slice(0, 2) + ':' + cleaned.slice(2);
  } else if (cleaned.includes(':')) {
    const parts = cleaned.split(':');
    let minutes = parts[0] ? parseInt(parts[0]) : 0;
    let seconds = parts[1] ? parseInt(parts[1]) : 0;

    if (maxSeconds !== undefined) {
      const totalSeconds = minutes * 60 + seconds;
      if (totalSeconds > maxSeconds) {
        minutes = Math.floor(maxSeconds / 60);
        seconds = maxSeconds % 60;
      }
    }

    minutes = Math.min(minutes, 59);
    seconds = Math.min(seconds, 59);

    const minutesStr = minutes.toString().padStart(2, '0');
    const secondsStr = parts[1] === undefined ? '' : seconds.toString().padStart(2, '0');
    return minutesStr + ':' + secondsStr;
  }

  return cleaned;
};

type EditSongPopupProps = {
  songUrl?: string;
  originalAssetName?: string;
  eventId?: number;
  setShowEditMusicPopup?: Dispatch<SetStateAction<boolean>>;
  setEditingSongUrl?: Dispatch<SetStateAction<string | undefined>>;
  setEditingAssetName?: Dispatch<SetStateAction<string | undefined>>;
  onSongSaved?: (asset: Asset) => void;
  closeModal?: () => void;
};

export const EditSongPopup = ({
  songUrl,
  originalAssetName,
  eventId,
  setShowEditMusicPopup,
  setEditingSongUrl,
  setEditingAssetName,
  onSongSaved,
  closeModal
}: EditSongPopupProps) => {
  const [t] = useTranslation('common');
  const queryClient = useQueryClient();

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [duration, setDuration] = useState<number>(0);
  const [start, setStart] = useState<number>(0);
  const [end, setEnd] = useState<number>(0);
  const [initialStart, setInitialStart] = useState<number>(0);
  const [initialEnd, setInitialEnd] = useState<number>(0);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [fadeInEnabled, setFadeInEnabled] = useState<boolean>(false);
  const [fadeOutEnabled, setFadeOutEnabled] = useState<boolean>(false);
  const [fadeInTime, setFadeInTime] = useState<string>('00:03');
  const [fadeOutTime, setFadeOutTime] = useState<string>('00:03');
  const [processing, setProcessing] = useState(false);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [isGeneratingWaveform, setIsGeneratingWaveform] = useState(false);
  const MAX_FADE_SECONDS = 5;
  const leftPct = duration ? (start / duration) * 100 : 0;
  const widthPct = duration ? ((end - start) / duration) * 100 : 0;
  const tolerance = 0.1;

  const { mutateAsync: createSasToken } = useCreateSasTokenMutation(queryClient, {
    onSuccess: () => undefined,
    onError: () => {
      showToast(ToastType.ERROR, t('sasTokenError'));
    }
  });

  const { mutateAsync: createAsset } = useCreateAssetMutation(queryClient, {
    onSuccess: () => undefined,
    onError: () => {
      showToast(ToastType.ERROR, t('assetError'));
    }
  });

  const loadWaveform = async (audioUrl: string) => {
    setIsGeneratingWaveform(true);
    setWaveformData([]);

    try {
      const data = await generateWaveformData(audioUrl);
      setWaveformData(data);
    } catch {
      setWaveformData([]);
    } finally {
      setIsGeneratingWaveform(false);
    }
  };

  const handleSaveTrim = async () => {
    if (!songUrl || end <= start) {
      showToast(ToastType.ERROR, t('invalidSongOrTimeRange'));
      return;
    }

    const trimDuration = end - start;
    if (trimDuration < MIN_DURATION) {
      showToast(ToastType.ERROR, t('minimumDurationRequired', { duration: MIN_DURATION }));
      return;
    }

    const fadeInSeconds = fadeInEnabled ? parseTimeToSeconds(fadeInTime) : 0;
    const fadeOutSeconds = fadeOutEnabled ? parseTimeToSeconds(fadeOutTime) : 0;

    setProcessing(true);
    try {
      const ffmpeg = await getFFmpeg();

      const inputName = 'input.mp3';
      const outputName = 'trimmed_song.mp3';

      const data = await fetchFile(songUrl);
      await ffmpeg.writeFile(inputName, data);

      let ffmpegArgs: string[];

      if (fadeInEnabled || fadeOutEnabled) {
        const filters = [];

        if (fadeInEnabled && fadeInSeconds > 0) {
          const fadeInFilter = `afade=type=in:start_time=${start}:d=${fadeInSeconds}`;
          filters.push(fadeInFilter);
        }

        if (fadeOutEnabled && fadeOutSeconds > 0) {
          const fadeOutFilter = `afade=type=out:start_time=${end - fadeOutSeconds}:d=${fadeOutSeconds}`;
          filters.push(fadeOutFilter);
        }

        if (filters.length > 0) {
          ffmpegArgs = [
            '-y',
            '-i',
            inputName,
            '-ss',
            String(start),
            '-t',
            String(trimDuration),
            '-af',
            filters.join(','),
            '-f',
            'mp3',
            outputName
          ];
        } else {
          ffmpegArgs = [
            '-y',
            '-i',
            inputName,
            '-ss',
            String(start),
            '-t',
            String(trimDuration),
            '-c:a',
            'copy',
            outputName
          ];
        }
      } else {
        ffmpegArgs = [
          '-y',
          '-i',
          inputName,
          '-ss',
          String(start),
          '-t',
          String(trimDuration),
          '-c:a',
          'copy',
          outputName
        ];
      }

      try {
        await ffmpeg.exec(ffmpegArgs);
      } catch (ffmpegError) {
        if (fadeInEnabled || fadeOutEnabled) {
          const basicArgs = [
            '-y',
            '-i',
            inputName,
            '-ss',
            String(start),
            '-t',
            String(trimDuration),
            '-c:a',
            'copy',
            outputName
          ];

          await ffmpeg.exec(basicArgs);
        } else {
          throw ffmpegError;
        }
      }

      const outputData = (await ffmpeg.readFile(outputName)) as Uint8Array;
      const audioBlob = new Blob([new Uint8Array(outputData)], { type: 'audio/mpeg' });

      const originalName = (originalAssetName || 'song').replace(/\.mp3$/i, '');
      const trimmedAssetName = `${originalName}_trimmed_${formatTime(start)}-${formatTime(end)}`;

      const createdAsset = await uploadTrimmedSong(audioBlob, trimmedAssetName);
      if (!createdAsset) {
        return;
      }

      onSongSaved?.(createdAsset);

      const previewUrl = URL.createObjectURL(audioBlob);
      setEditingSongUrl?.(previewUrl);

      setShowEditMusicPopup?.(false);
      closeModal?.();
    } finally {
      setProcessing(false);
    }
  };

  const uploadTrimmedSong = async (blob: Blob, assetName: string): Promise<Asset | null> => {
    if (!eventId) {
      showToast(ToastType.ERROR, t('eventIdRequired'));
      return null;
    }

    try {
      const sasTokenPayload = {
        id: eventId,
        assetName: '',
        extension: '.mp3',
        isPublic: false,
        isThumbnail: false,
        blobLocation: BlobLocation.EVENTS
      };

      const sasTokenResponse = await createSasToken(sasTokenPayload);
      if (!sasTokenResponse?.data) {
        throw new Error('Failed to get SAS token');
      }

      const sasData = sasTokenResponse.data;
      const assetBlobClient = getAssetBlobClient(sasTokenResponse);

      if (!assetBlobClient) {
        return null;
      }

      const encodedAssetName = encodeURIComponent(assetName + '.mp3')
        .replace(/['()]/g, escape)
        .replace(/\*/g, '%2A')
        .replace(/%20/g, ' ');

      const contentDisposition = `attachment; filename="${encodedAssetName}"`;
      try {
        await assetBlobClient.uploadBrowserData(blob, {
          blobHTTPHeaders: {
            blobContentType: 'audio/mpeg',
            blobContentDisposition: contentDisposition
          }
        });
      } catch (err) {
        throw new Error(
          'Azure upload failed: ' + (err instanceof Error ? err.message : 'Unknown error')
        );
      }

      const assetPayload = {
        eventId: eventId,
        assetType: AssetType.AUDIO,
        assetScope: AssetScope.PRIVATE,
        assetName: assetName,
        assetUrl: `${sasData.baseUrl}/${sasData.path}`,
        thumbnailUrl: '',
        assetSize: blob.size,
        additionalData: JSON.stringify({
          originalSong: songUrl,
          startTime: start,
          endTime: end,
          trimmedDuration: Math.round(end - start)
        }),
        duration: Math.round(end - start),
        tags: []
      };

      const assetResponse = await createAsset(assetPayload);
      if (!assetResponse?.data) {
        throw new Error('Failed to create asset');
      }

      showToast(ToastType.SUCCESS, t('uploadSuccess', { id: assetResponse.data.id }));
      return assetResponse.data as unknown as Asset;
    } catch (error) {
      if (error instanceof Error) {
        showToast(ToastType.ERROR, t('uploadFailed', { message: error.message }));
      }
      return null;
    }
  };

  const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

  const applyFadeEffect = (current: number) => {
    if (!audioRef.current) {
      return;
    }

    const fadeInSeconds = parseTimeToSeconds(fadeInTime);
    const fadeOutSeconds = parseTimeToSeconds(fadeOutTime);

    if (fadeInEnabled && current >= start && current < start + fadeInSeconds) {
      const progress = (current - start) / fadeInSeconds;
      audioRef.current.volume = clamp(progress, 0, 1);
    } else if (fadeOutEnabled && current > end - fadeOutSeconds && current <= end) {
      const progress = (end - current) / fadeOutSeconds;
      audioRef.current.volume = clamp(progress, 0, 1);
    } else {
      audioRef.current.volume = 1;
    }
  };

  const formatTime = (songTime: number) => {
    const minutes = Math.floor(songTime / 60);
    const seconds = Math.floor(songTime % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const handleStartChange = (val: number) => {
    const maxStartValue = currentTime > start ? currentTime : end - MIN_DURATION;
    const next = clamp(val, 0, Math.min(maxStartValue, end - MIN_DURATION));
    setStart(next);

    const hasChanged = next !== initialStart || end !== initialEnd;
    setHasChanges(hasChanged);
  };

  const handleEndChange = (val: number) => {
    const minEndValue = currentTime < end ? currentTime : start + MIN_DURATION;
    const next = clamp(val, Math.max(minEndValue, start + MIN_DURATION), duration);
    setEnd(next);

    const hasChanged = start !== initialStart || next !== initialEnd;
    setHasChanges(hasChanged);
  };

  const handlePlayPause = async () => {
    if (!audioRef.current) {
      return;
    }
    audioRef.current.volume = 0;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      const currentPos = audioRef.current.currentTime;

      if (currentPos < start - tolerance || currentPos > end + tolerance) {
        audioRef.current.currentTime = start;
        setCurrentTime(start);
      }

      try {
        setIsPlaying(true);
        await audioRef.current.play();
      } catch (error) {
        setIsPlaying(false);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) {
      return;
    }

    const current = audioRef.current.currentTime;
    setCurrentTime(current);
    applyFadeEffect(current);

    if (current >= end + tolerance || current < start - tolerance) {
      audioRef.current.pause();
      setIsPlaying(false);
      audioRef.current.volume = 1;
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.currentTime = start;
      audioRef.current.volume = 1;
      setCurrentTime(start);
    }
  };

  useEffect(() => {
    if (!audioRef.current || isPlaying) {
      return;
    }

    const current = audioRef.current.currentTime;

    if (current < start - tolerance || current > end + tolerance) {
      audioRef.current.currentTime = start;
      setCurrentTime(start);
    }
  }, [start, end]);

  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }

    drawWaveformOnCanvas({
      canvas: canvasRef.current,
      waveformData,
      duration,
      currentTime
    });
  }, [waveformData, currentTime, duration]);

  const createDragHandler = (changeHandler: (time: number) => void) => {
    return (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const container = e.currentTarget.parentElement;
      if (!container) {
        return;
      }

      let isDragging = true;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isDragging) {
          return;
        }

        const rect = container.getBoundingClientRect();
        const x = moveEvent.clientX - rect.left;
        const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
        const time = (percent / 100) * duration;
        changeHandler(time);
      };

      const handleMouseUp = () => {
        isDragging = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };

      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };
  };

  return (
    <Modal
      isOpen={true}
      onClose={() => {
        setShowEditMusicPopup?.(false);
        closeModal?.();
      }}
      modalClasses='!h-[430px] '
      containerClasses='!h-[600px]  max-h-[90vh] !overflow-y-visible'
      label={t('editSong')}
      disableOverlayClick>
      <div className='w-full border-t' />
      <div
        className='mt-4 w-[560px] max-w-[92vw] divide-x-0 rounded-lg px-4'
        data-testid={TestIds.eventMusic.trimModal}>
        <audio
          ref={audioRef}
          src={songUrl}
          onLoadedMetadata={(e) => {
            const audioElement = e.currentTarget as HTMLAudioElement;
            setDuration(audioElement.duration);
            setStart(0);
            setEnd(audioElement.duration);
            setInitialStart(0);
            setInitialEnd(audioElement.duration);
            setHasChanges(false);
            if (songUrl) {
              loadWaveform(songUrl);
            }
          }}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleAudioEnded}
          style={{ display: 'none' }}
        />
        <div className='mb-5 flex w-full items-center justify-center text-sm'>
          <div className='mr-2 w-full truncate text-sm font-bold'>{originalAssetName}</div>
        </div>
        <div className='mb-2 flex items-center justify-between text-sm'>
          <p>
            {t('start')}: <strong>{formatTime(start)}</strong>
          </p>
          <p data-testid={TestIds.eventMusic.trimDurationLabel}>
            {t('playlistDuration')}: <strong>{formatTime(Math.max(0, end - start))}</strong>
          </p>
          <p>
            {t('end')}: <strong>{formatTime(end)}</strong>
          </p>
        </div>
        <div className='relative mb-2 px-2'>
          <div className='relative h-20 w-full' data-testid={TestIds.eventMusic.trimTrack}>
            <DraggableElement
              position={`${leftPct}%`}
              isLabel={true}
              onMouseDown={createDragHandler(handleStartChange)}
              className='bg-accent-green hover:bg-green-600'
              dataTestId={TestIds.eventMusic.trimStartHandle}>
              {formatTime(start)}
            </DraggableElement>
            <DraggableElement
              position={`${leftPct + widthPct}%`}
              isLabel={true}
              onMouseDown={createDragHandler(handleEndChange)}
              className='bg-accent-red hover:bg-red-600'
              dataTestId={TestIds.eventMusic.trimEndHandle}>
              {formatTime(end)}
            </DraggableElement>
            <canvas
              ref={canvasRef}
              width={560}
              height={120}
              className='absolute top-3 h-16 w-full rounded-md'
            />
            {isGeneratingWaveform && (
              <div className='absolute top-3 flex h-16 w-full items-center justify-center rounded-md bg-gray-200'>
                <Spinner />
              </div>
            )}
            <div
              className='pointer-events-none absolute top-3 h-16 rounded bg-blue-500 opacity-25'
              style={{
                left: `${leftPct}%`,
                width: `${widthPct}%`
              }}
            />
            {fadeInEnabled && fadeInTime && (
              <div
                className='pointer-events-none absolute top-3 h-16 rounded-l bg-blue-500 opacity-25'
                style={{
                  left: `${leftPct}%`,
                  width: `${Math.min((parseTimeToSeconds(fadeInTime) / (end - start)) * widthPct, widthPct / 2)}%`
                }}
              />
            )}
            {fadeOutEnabled && fadeOutTime && (
              <div
                className='pointer-events-none absolute top-3 h-16 rounded-r bg-blue-500 opacity-25'
                style={{
                  left: `${leftPct + widthPct - Math.min((parseTimeToSeconds(fadeOutTime) / (end - start)) * widthPct, widthPct / 2)}%`,
                  width: `${Math.min((parseTimeToSeconds(fadeOutTime) / (end - start)) * widthPct, widthPct / 2)}%`
                }}
              />
            )}
            <DraggableElement
              position={`${leftPct}%`}
              onMouseDown={createDragHandler(handleStartChange)}
              className='w-0.5 bg-accent-green'
            />
            <DraggableElement
              position={`${leftPct + widthPct}%`}
              onMouseDown={createDragHandler(handleEndChange)}
              className='w-0.5 bg-accent-red'
            />
            <DraggableElement
              position={`${leftPct}%`}
              onMouseDown={createDragHandler(handleStartChange)}
              className='top-[72px] z-20 !size-2 -translate-x-1/2 rounded-full bg-accent-green'
            />

            <DraggableElement
              position={`${leftPct + widthPct}%`}
              onMouseDown={createDragHandler(handleEndChange)}
              className='top-[72px] z-20 !size-2 -translate-x-1/2 rounded-md bg-accent-red'
            />
          </div>
        </div>
        <div className='mb-4 px-2'>
          <div className='relative mb-3 h-6 w-full rounded-md bg-gray-200'>
            <div
              className='absolute h-6 rounded bg-blue-500 opacity-20'
              style={{
                left: `${leftPct}%`,
                width: `${widthPct}%`
              }}
            />
            {currentTime >= start && currentTime <= end && (
              <div
                className='absolute h-6 rounded bg-blue-400 opacity-[.64]'
                style={{
                  left: `${leftPct}%`,
                  width: `${((currentTime - start) / (end - start)) * widthPct}%`
                }}
              />
            )}
            <div
              className='absolute top-1/2 size-6 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full border-2 border-blue-900 active:cursor-grabbing'
              style={{
                left: `${(currentTime / duration) * 100}%`,
                backgroundColor: '#c0ccec',
                zIndex: 50
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const container = e.currentTarget.parentElement;
                if (!container || !audioRef.current) {
                  return;
                }
                let dragging = true;
                const onMove = (moveEvent: MouseEvent) => {
                  if (!dragging || !audioRef.current) {
                    return;
                  }
                  const rect = container.getBoundingClientRect();
                  const x = moveEvent.clientX - rect.left;
                  const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
                  const time = (pct / 100) * duration;

                  const constrainedTime = Math.max(start, Math.min(end, time));

                  audioRef.current.currentTime = constrainedTime;
                  setCurrentTime(constrainedTime);
                };
                const onUp = () => {
                  dragging = false;
                  document.removeEventListener('mousemove', onMove);
                  document.removeEventListener('mouseup', onUp);
                  document.body.style.userSelect = '';
                  document.body.style.cursor = '';
                };

                document.body.style.userSelect = 'none';
                document.body.style.cursor = 'grabbing';
                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
              }}
            />
          </div>
          <div className='mb-4 space-y-4'>
            <div className='flex flex-row items-center justify-between'>
              <div className='flex flex-col items-start'>
                <Label className='mb-1 mt-2' type={LABEL_TYPE.PARAGRAPH2} text={t('fadeIn')} />
                <div className='flex flex-row items-center gap-2'>
                  <ToggleSwitch
                    handleToggle={() => setFadeInEnabled(!fadeInEnabled)}
                    toggled={fadeInEnabled}
                    variant={TOGGLE_THEMES.LIGHT}
                    containerClass='!w-32 !max-width-32'
                  />
                  <Input
                    disabled={!fadeInEnabled}
                    id='fadeInTime'
                    label=''
                    type='text'
                    value={fadeInTime}
                    handleChange={(value: string) => {
                      const validated = validateTimeInput(value, MAX_FADE_SECONDS);
                      setFadeInTime(validated);
                    }}
                    className='!w-16'
                  />
                </div>
              </div>
              <div className='mt-8 flex justify-center'>
                <button
                  onClick={handlePlayPause}
                  disabled={!duration}
                  className='flex size-10 items-center justify-center rounded-full border border-blue-500 bg-white text-blue-500 opacity-[.5] disabled:cursor-not-allowed disabled:bg-gray-300'>
                  {isPlaying ? (
                    <PauseIcon className='size-5 text-blue-500' />
                  ) : (
                    <PlayIcon className='pl-0.5' />
                  )}
                </button>
              </div>
              <div className='flex flex-col items-end'>
                <Label className='mb-1 mt-2' type={LABEL_TYPE.PARAGRAPH2} text={t('fadeOut')} />
                <div className='flex flex-row items-center justify-end gap-2'>
                  <ToggleSwitch
                    handleToggle={() => setFadeOutEnabled(!fadeOutEnabled)}
                    toggled={fadeOutEnabled}
                    variant={TOGGLE_THEMES.LIGHT}
                    containerClass='!w-32 !max-width-32'
                  />
                  <Input
                    id='fadeOutTime'
                    label=''
                    type='text'
                    value={fadeOutTime}
                    disabled={!fadeOutEnabled}
                    handleChange={(value: string) => {
                      const validated = validateTimeInput(value, MAX_FADE_SECONDS);
                      setFadeOutTime(validated);
                    }}
                    className='!w-16'
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='mt-6 flex justify-end gap-2'>
          <Button
            className='w-32'
            variant={ButtonVariant.QUATERNARY}
            handleOnClick={() => {
              setShowEditMusicPopup?.(false);
              setEditingSongUrl?.(undefined);
              setEditingAssetName?.(undefined);
              closeModal?.();
            }}>
            {t('close')}
          </Button>

          <Button
            handleOnClick={handleSaveTrim}
            disabled={processing || (!hasChanges && !fadeOutEnabled && !fadeInEnabled)}
            className='w-32'
            data-testid={TestIds.eventMusic.trimSaveButton}>
            {t('save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
